/**
 * ACCOUNT SETTINGS (TS-33) — Applicazione "Gestione Account" del pannello
 * impostazioni: permette all'utente di configurare il proprio profilo.
 *
 * Navigazione orizzontale a tab: Profilo (default) · Task · Sicurezza.
 *
 * STATO: Profilo e Sicurezza salvano davvero (PATCH /user/profile e
 * POST /user/updatePassword). Task è ancora uno stub (toast).
 *
 * La tab EMAIL non c'è. C'era, e mostrava solo «prossimamente»: una tab che si
 * apre su una promessa insegna a non fidarsi delle altre, e su una pagina di
 * impostazioni — dove ci si va per cambiare qualcosa — è peggio che altrove.
 * Torna quando ha un contenuto, non prima. La chiave
 * `userSettings.account.tabs.email` resta nei locali del core apposta, senza
 * consumatori: il giorno in cui la tab rientra, la traduzione c'è già.
 */

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { useCurrentGroupId } from '../../auth/authSelectors';
import { useToast } from '../useToast';
import { useLanguage } from '../useLanguage';
import { resolvePortraitURL } from '../../api/portrait';
import { ImageUploadField } from './ImageUploadField';
import { PasswordChangeForm } from '../PasswordChangeForm';
import { updateProfile, fetchUserSettings, updateUserSettings } from '../../auth/authSlice';
import { teamApi, type Team } from '../../entities/team/teamApi';
import { DEADLINE_PRESETS, REMINDER_PRESETS, type ReminderPreset } from '../../entities/task/taskTypes';
import type { UpdateProfilePayload } from '../../auth/types';

type AccountTab = 'profile' | 'task' | 'security';

// Lingue configurate sul CRM (locali supportati: it_IT, en_US). Allineato a
// User.languageId (formato Liferay).
const LANGUAGES = [
  { value: 'it_IT', label: 'Italiano' },
  { value: 'en_US', label: 'English' },
];

export function AccountSettings() {
  const { t } = useTranslation();
  const [tab, setTab] = useState<AccountTab>('profile');

  return (
    <div className="account-settings">
      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v as AccountTab)}
        className="account-settings__tabs"
      >
        <Tab value="profile" label={t('userSettings.account.tabs.profile')} className="tab-compact" />
        <Tab value="task" label={t('userSettings.account.tabs.task')} className="tab-compact" />
        <Tab value="security" label={t('userSettings.account.tabs.security')} className="tab-compact" />
      </Tabs>

      {tab === 'profile' && <ProfileTab />}
      {tab === 'task' && <TaskDefaultsTab />}
      {tab === 'security' && <SecurityTab />}
    </div>
  );
}

// ── Tab Profilo ────────────────────────────────────────────────────────────
function ProfileTab() {
  const { t } = useTranslation();
  const toast = useToast();
  const { changeLanguage } = useLanguage();
  const dispatch = useAppDispatch();
  const user = useAppSelector((state) => state.auth.user);
  const groupId = useCurrentGroupId();
  const userId = user?.userId;
  // Ruolo CRM globale dell'utente: i Team di Liferay non hanno ruoli per-membro,
  // quindi il "ruolo ricoperto" mostrato accanto a ogni team è questo (uguale su tutti).
  const isAdmin = user?.administrator ?? false;
  const [saving, setSaving] = useState(false);

  // Team del sito corrente a cui l'utente appartiene (blocco read-only in fondo).
  const [teams, setTeams] = useState<Team[]>([]);
  const [teamsLoading, setTeamsLoading] = useState(false);

  useEffect(() => {
    if (!groupId || !userId) return;
    let active = true;
    setTeamsLoading(true);
    void (async () => {
      try {
        const all = await teamApi.getGroupTeams(groupId);
        const mine = all
          .filter((tm) => tm.members.some((m) => m.userId === userId))
          .sort((a, b) => a.name.localeCompare(b.name));
        if (active) setTeams(mine);
      } catch (err) {
        if (active) toast({ severity: 'error', message: err instanceof Error ? err.message : String(err) });
      } finally {
        if (active) setTeamsLoading(false);
      }
    })();
    return () => { active = false; };
  }, [groupId, userId, toast]);

  const roleLabel = t(`userSettings.usersTeam.roles.${isAdmin ? 'admin' : 'standard'}`);

  const [firstName, setFirstName] = useState(user?.firstName ?? '');
  const [lastName, setLastName] = useState(user?.lastName ?? '');
  const [email, setEmail] = useState(user?.emailAddress ?? '');
  const [languageId, setLanguageId] = useState(user?.languageId ?? 'it_IT');
  // Foto profilo: photoPreview è il ritaglio prodotto dal cropper (gestito da
  // ImageUploadField). Data URL, inviato come base64 al salvataggio.
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);

  // Validazione minima lato client: nome/cognome non vuoti ed email formalmente valida
  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
  const canSave =
    firstName.trim().length > 0 && lastName.trim().length > 0 && emailValid && !saving;

  async function handleSave() {
    if (!user || !canSave) return;
    setSaving(true);
    try {
      const payload: UpdateProfilePayload = {
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        emailAddress: email.trim(),
        languageId,
        // Foto inviata solo se scelta: data URL -> base64 puro (drop del prefisso).
        ...(photoPreview ? { profileFile: photoPreview.replace(/^data:[^,]+,/, '') } : {}),
      };
      await dispatch(updateProfile(payload)).unwrap();
      // Applica subito la lingua scelta in UI (it_IT/en_US -> it/en) + persiste in localStorage
      changeLanguage(languageId.split('_')[0]);
      setPhotoPreview(null);
      toast({ severity: 'success', message: t('common.saved') });
    } catch (err) {
      toast({ severity: 'error', message: err instanceof Error ? err.message : String(err) });
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="account-settings__panel account-settings__panel--profile">
      <div className="account-settings__profile-grid">
        {/* Colonna sinistra: form profilo modificabile */}
        <div className="account-settings__profile-main">
          <ImageUploadField
            value={photoPreview}
            onChange={setPhotoPreview}
            currentSrc={resolvePortraitURL(user?.portraitURL)}
            selectLabel={t('userSettings.profile.selectPhoto')}
            repositionLabel={t('userSettings.profile.reposition')}
            alt={user?.fullName ?? ''}
            shape="circular"
          />

          <TextField
            label={t('userSettings.profile.firstName')}
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            fullWidth
            className="account-settings__field"
          />
          <TextField
            label={t('userSettings.profile.lastName')}
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            fullWidth
            className="account-settings__field"
          />
          <TextField
            type="email"
            label={t('userSettings.profile.email')}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            fullWidth
            className="account-settings__field"
            error={email.length > 0 && !emailValid}
            helperText={email.length > 0 && !emailValid ? t('userSettings.profile.emailInvalid') : undefined}
          />
          <TextField
            select
            label={t('userSettings.profile.language')}
            value={languageId}
            onChange={(e) => setLanguageId(e.target.value)}
            fullWidth
            className="account-settings__field"
          >
            {LANGUAGES.map((l) => (
              <MenuItem key={l.value} value={l.value}>{l.label}</MenuItem>
            ))}
          </TextField>

          <div className="account-settings__actions">
            <Button variant="contained" onClick={handleSave} disabled={!canSave}>{t('common.save')}</Button>
          </div>
        </div>

        {/* Colonna destra: informazioni read-only (ruolo CRM + team di appartenenza) */}
        <aside className="account-settings__profile-side">
          <h3 className="account-settings__section-title">{t('userSettings.profile.roleTitle')}</h3>
          <Chip
            label={roleLabel}
            size="small"
            variant="outlined"
            color={isAdmin ? 'primary' : 'default'}
          />

          <h3 className="account-settings__section-title">{t('userSettings.profile.teamsTitle')}</h3>
          {teamsLoading ? null : teams.length === 0 ? (
            <p className="text-muted">{t('userSettings.profile.teamsEmpty')}</p>
          ) : (
            <ul className="account-settings__teams">
              {teams.map((tm) => (
                <li key={tm.teamId} className="account-settings__team-row">
                  <span className="account-settings__team-info">
                    <span className="account-settings__team-name">{tm.name}</span>
                    {tm.description && (
                      <span className="account-settings__team-desc">{tm.description}</span>
                    )}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </aside>
      </div>
    </div>
  );
}

// ── Tab Task (impostazioni predefinite) ────────────────────────────────────
// Le sole scorciatoie RELATIVE dell'elenco condiviso: "personalizzata" apre un selettore di data,
// e una data fissa non è un valore predefinito sensato — sarebbe già passata al secondo task.
const RELATIVE_DEADLINE_PRESETS = DEADLINE_PRESETS.filter((p) => p.days !== null);
// Sottoinsieme dichiarato per VALORI e non filtrato su una proprietà: `none` e `custom` hanno
// entrambi `offsetMinutes` null pur essendo casi opposti, quindi nessun predicato sull'elenco
// separa le voci come serve qui. Restano fuori `custom` (un promemoria predefinito non può essere
// una data fissa) e `week1` (una settimana prima è una scelta del singolo task, non un default).
const DEFAULT_REMINDER_VALUES: ReminderPreset[] = [
  'none',
  'atExpiration',
  'min30',
  'hour1',
  'day1',
];
const DEFAULT_REMINDER_PRESETS = REMINDER_PRESETS.filter((p) =>
  DEFAULT_REMINDER_VALUES.includes(p.value),
);

function TaskDefaultsTab() {
  const { t } = useTranslation();
  const toast = useToast();
  const dispatch = useAppDispatch();
  const userSettings = useAppSelector((state) => state.auth.userSettings);

  const [deadline, setDeadline] = useState('threeDays');
  const [time, setTime] = useState('17:00');
  const [reminder, setReminder] = useState('atExpiration');
  const [saving, setSaving] = useState(false);

  // Carico le preferenze all'apertura del tab (skip se già nello store)
  useEffect(() => {
    if (userSettings) return;
    let active = true;
    void (async () => {
      try {
        await dispatch(fetchUserSettings()).unwrap();
      } catch (err) {
        if (active) toast({ severity: 'error', message: err instanceof Error ? err.message : String(err) });
      }
    })();
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  // Sincronizzo i campi del form quando arrivano le preferenze dal backend
  useEffect(() => {
    if (userSettings) {
      setDeadline(userSettings.taskDeadline);
      setTime(userSettings.taskTime);
      setReminder(userSettings.taskReminder);
    }
  }, [userSettings]);

  async function handleSave() {
    setSaving(true);
    try {
      await dispatch(updateUserSettings({
        taskDeadline: deadline,
        taskTime: time,
        taskReminder: reminder,
      })).unwrap();
      toast({ severity: 'success', message: t('common.saved') });
    } catch (err) {
      toast({ severity: 'error', message: err instanceof Error ? err.message : String(err) });
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="account-settings__panel">
      <p className="account-settings__hint">{t('userSettings.task.appliesOnlyToYou')}</p>
      <h3 className="account-settings__section-title">{t('userSettings.task.defaultsTitle')}</h3>

      <TextField
        select
        label={t('userSettings.task.expirationDate')}
        value={deadline}
        onChange={(e) => setDeadline(e.target.value)}
        fullWidth
        className="account-settings__field"
      >
        {RELATIVE_DEADLINE_PRESETS.map((p) => (
          <MenuItem key={p.value} value={p.value}>{t(p.label)}</MenuItem>
        ))}
      </TextField>
      <TextField
        type="time"
        label={t('userSettings.task.expirationTime')}
        value={time}
        onChange={(e) => setTime(e.target.value)}
        fullWidth
        className="account-settings__field"
        slotProps={{ inputLabel: { shrink: true } }}
      />
      <TextField
        select
        label={t('userSettings.task.reminder')}
        value={reminder}
        onChange={(e) => setReminder(e.target.value)}
        fullWidth
        className="account-settings__field"
      >
        {DEFAULT_REMINDER_PRESETS.map((p) => (
          <MenuItem key={p.value} value={p.value}>{t(p.label)}</MenuItem>
        ))}
      </TextField>

      <div className="account-settings__actions">
        <Button variant="contained" onClick={handleSave} disabled={saving}>{t('common.save')}</Button>
      </div>
    </div>
  );
}

// ── Tab Sicurezza (cambio password) ────────────────────────────────────────
// Il form vive in PasswordChangeForm, condiviso con la pagina di cambio
// obbligatorio (TS-119): qui resta solo la cornice del pannello impostazioni.
function SecurityTab() {
  const { t } = useTranslation();

  return (
    <div className="account-settings__panel">
      <h3 className="account-settings__section-title">{t('userSettings.security.changePassword')}</h3>
      <PasswordChangeForm />
    </div>
  );
}
